import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import Map from '../components/Map';
import FilterPanel from '../components/FilterPanel';
import SpotForm from '../components/SpotForm';
import SeekerForm from '../components/SeekerForm';
import RentPinForm from '../components/RentPinForm';
import ReportPinModal from '../components/ReportPinModal';
import InterestModal from '../components/InterestModal';
import CommentsModal from '../components/CommentsModal';
import TourOverlay from '../components/TourOverlay';
import HelpTip from '../components/HelpTip';
import { api } from '../services/api';
import type { ReportablePinType } from '../services/report';
import { attachInterestClickHandler, attachCommentsClickHandler, COMMENT_PIN_TYPE_MAP } from '../services/interest';
import { trackEvent, mapApiErrorToCode } from '../services/analytics';
import type { PinType, ViewMode } from '../services/analytics';
import type { CommentPinType } from '../types';
import { useTour, useAutoStartTour } from '../hooks/useTour';
import type { TourStepId } from '../services/tour';
import {
  loadSavedLocation,
  saveLocation,
  detectGpsLocation,
  detectIpLocation,
  getViewportForLocation,
  isWithinIndia,
  DEFAULT_VIEWPORT,
  SEARCH_RADIUS_METERS,
  CITY_ZOOM,
} from '../services/location';
import type { ParkingSpot, SeekerPin, RentPin, Filters, UserLocation, Viewport, GeocodeResult } from '../types';
import LocationSearch from '../components/LocationSearch';

const DEFAULT_FILTERS: Filters = {
  vehicle_type: '',
  parking_type: '',
  duration: '',
  min_price: '',
  max_price: '',
  covered: false,
  security: [],
  access_24x7: false,
  ev: false,
  free: 'any',
};

const SHARED_PIN_ZOOM = 16;

type FormMode = PinType | null;

const LEGEND_ITEMS = [
  { color: 'bg-green-500', label: 'Free Parking' },
  { color: 'bg-amber-500', label: 'Paid Parking' },
  { color: 'bg-blue-600', label: 'Seeker' },
  { color: 'bg-purple-600', label: 'Price Data' },
];

const ACTION_ITEMS: { mode: FormMode; label: string; icon: string; color: string }[] = [
  { mode: 'spot', label: 'List My Parking Spot', icon: '🅿️', color: 'bg-parksy-600 hover:bg-parksy-700' },
  { mode: 'seeker', label: 'Find Parking', icon: '🔍', color: 'bg-blue-600 hover:bg-blue-700' },
  { mode: 'rent', label: 'Pin What I Pay', icon: '📍', color: 'bg-purple-600 hover:bg-purple-700' },
];

export default function Home() {
  const params = useParams<{ type?: string; id?: string }>();
  const [spots, setSpots] = useState<ParkingSpot[]>([]);
  const [seekers, setSeekers] = useState<SeekerPin[]>([]);
  const [rentPins, setRentPins] = useState<RentPin[]>([]);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [formLatLng, setFormLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [reportTarget, setReportTarget] = useState<{ type: ReportablePinType; id: string } | null>(null);
  const [interestTarget, setInterestTarget] = useState<{ spotId: string } | null>(null);
  const [commentsTarget, setCommentsTarget] = useState<{ pinType: PinType; pinTypeApi: CommentPinType; pinId: string } | null>(null);
  const [stats, setStats] = useState({ parking_spots: 0, seeker_pins: 0, rent_pins: 0, matches: 0 });
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [viewport, setViewport] = useState<Viewport>(DEFAULT_VIEWPORT);
  const [locationLoading, setLocationLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [focusedPin, setFocusedPin] = useState<{ type: PinType; id: string } | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);

  const tour = useTour();
  const setTourStart = useCallback((fromStep: number = 0) => {
    tour.start(fromStep);
  }, [tour]);
  useAutoStartTour(true, mapReady, tour.start);

  useEffect(() => {
    if (!tour.isRunning) return;
    const id = tour.currentStep?.id;
    if (id === 'view_modes') {
      setDrawerOpen(true);
    } else if (id === 'legend' || id === 'fab' || id === 'finish') {
      setDrawerOpen(false);
    }
  }, [tour.isRunning, tour.currentStep?.id]);

  const prevTourStepRef = useRef<TourStepId | null>(null);
  const prevTourRunningRef = useRef(false);
  useEffect(() => {
    if (tour.isRunning) {
      if (tour.currentStep?.id === 'legend') {
        setLegendOpen(true);
      } else if (prevTourStepRef.current === 'legend') {
        setLegendOpen(false);
      }
      if (tour.currentStep?.id === 'fab') {
        setFabOpen(true);
      } else if (prevTourStepRef.current === 'fab') {
        setFabOpen(false);
      }
    } else if (prevTourRunningRef.current) {
      setLegendOpen(false);
      setFabOpen(false);
    }
    prevTourStepRef.current = tour.currentStep?.id ?? null;
    prevTourRunningRef.current = tour.isRunning;
  }, [tour.isRunning, tour.currentStep?.id]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const locationParams = userLocation
        ? { lat: userLocation.lat, lng: userLocation.lng, radius: SEARCH_RADIUS_METERS }
        : undefined;
      if (viewMode === 'all') {
        const [spotsData, seekersData, rentData] = await Promise.all([
          api.getSpots({ ...filters, ...locationParams }),
          api.getSeekers(locationParams),
          api.getRentPins(locationParams),
        ]);
        setSpots(spotsData);
        setSeekers(seekersData);
        setRentPins(rentData);
      } else if (viewMode === 'spots') {
        const data = await api.getSpots({ ...filters, ...locationParams });
        setSpots(data);
      } else if (viewMode === 'seekers') {
        const data = await api.getSeekers(locationParams);
        setSeekers(data);
      } else {
        const data = await api.getRentPins(locationParams);
        setRentPins(data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [viewMode, filters, userLocation]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    api.getOverallStats().then(setStats).catch(console.error);

    if (params.type && params.id) {
      return;
    }

    const saved = loadSavedLocation();
    if (saved) {
      setUserLocation(saved);
      setViewport(getViewportForLocation(saved));
      return;
    }

    // Silent IP-based approximation on first load; no GPS prompt
    detectIpLocation()
      .then((ip) => {
        if (isWithinIndia(ip.lat, ip.lng)) {
          const location = saveLocation({ lat: ip.lat, lng: ip.lng, source: 'ip' });
          setUserLocation(location);
          setViewport(getViewportForLocation(location));
          trackEvent('location_detect_outcome', { source: 'ip', result: 'success' });
        } else {
          trackEvent('location_detect_outcome', { source: 'ip', result: 'outside_india' });
        }
      })
      .catch(() => {
        trackEvent('location_detect_outcome', { source: 'ip', result: 'error' });
      });
  }, []);

  useEffect(() => {
    const rawType = params.type;
    const id = params.id;
    if (!rawType || !id) {
      setFocusedPin(null);
      return;
    }

    if (rawType !== 'spot' && rawType !== 'seeker' && rawType !== 'rent') {
      showSuccess('Invalid share link');
      return;
    }

    const pinType = rawType as PinType;
    setViewMode('all');
    trackEvent('share_link_open', { pin_type: pinType });

    const fetcher =
      pinType === 'spot'
        ? api.getSpot(id)
        : pinType === 'seeker'
        ? api.getSeeker(id)
        : api.getRentPin(id);

    fetcher
      .then((pin) => {
        setViewport({ center: [pin.lng, pin.lat], zoom: SHARED_PIN_ZOOM });
        setFocusedPin({ type: pinType, id: pin.id });
      })
      .catch((err) => {
        console.error('Failed to load shared pin:', err);
        showSuccess('This pin is no longer available');
      });
  }, [params.type, params.id]);

  useEffect(() => {
    if (fabOpen) {
      trackEvent('fab_opened', {});
    }
  }, [fabOpen]);

  useEffect(() => {
    if (drawerOpen) {
      trackEvent('drawer_open', {});
    }
  }, [drawerOpen]);

  useEffect(() => {
    trackEvent('legend_toggle', { state: legendOpen ? 'open' : 'closed' });
  }, [legendOpen]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ pinType: ReportablePinType; pinId: string }>).detail;
      if (!detail) return;
      if (detail.pinType !== 'spot' && detail.pinType !== 'rent') return;
      setReportTarget({ type: detail.pinType, id: detail.pinId });
    };
    window.addEventListener('parksy:open-report', handler);
    return () => window.removeEventListener('parksy:open-report', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ spotId: string }>).detail;
      if (!detail?.spotId) return;
      setInterestTarget({ spotId: detail.spotId });
    };
    window.addEventListener('parksy:open-interest', handler);
    return () => window.removeEventListener('parksy:open-interest', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ pinType: PinType; pinId: string; pinTypeApi: CommentPinType }>).detail;
      if (!detail) return;
      if (detail.pinType !== 'spot' && detail.pinType !== 'seeker' && detail.pinType !== 'rent') return;
      setCommentsTarget({
        pinType: detail.pinType,
        pinTypeApi: detail.pinTypeApi || COMMENT_PIN_TYPE_MAP[detail.pinType],
        pinId: detail.pinId,
      });
    };
    window.addEventListener('parksy:open-comments', handler);
    return () => window.removeEventListener('parksy:open-comments', handler);
  }, []);

  useEffect(() => {
    attachInterestClickHandler();
    attachCommentsClickHandler();
  }, []);

  const setViewModeTracked = (next: ViewMode) => {
    if (next !== viewMode) {
      trackEvent('view_mode_change', { from: viewMode, to: next });
      setViewMode(next);
    }
  };

  // Close FAB when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fabRef.current && !fabRef.current.contains(e.target as Node)) {
        setFabOpen(false);
      }
    };
    if (fabOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [fabOpen]);

  const handleMapClick = (lat: number, lng: number) => {
    if (formMode) {
      trackEvent('map_click_to_drop', { form_mode: formMode });
    }
    setFormLatLng({ lat, lng });
  };

  const startAddingPin = (mode: FormMode) => {
    if (mode) {
      trackEvent('add_pin_started', { pin_type: mode });
    }
    setFormMode(mode);
    setFormLatLng(null);
    setDrawerOpen(false);
    setFabOpen(false);
  };

  const cancelForm = () => {
    if (formMode) {
      trackEvent('add_pin_cancelled', {
        pin_type: formMode,
        step: formLatLng ? 'after_pin_drop' : 'before_pin_drop',
      });
    }
    setFormMode(null);
    setFormLatLng(null);
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  const handleSpotSubmit = async (data: Omit<ParkingSpot, 'id' | 'created_at' | 'interest_count' | 'comment_count'> & { email: string; phone: string }) => {
    trackEvent('form_submit_attempt', { pin_type: 'spot' });
    setFormLoading(true);
    try {
      await api.createSpot(data);
      trackEvent('form_submit_success', { pin_type: 'spot' });
      setFormMode(null);
      setFormLatLng(null);
      showSuccess('Parking spot listed!');
      fetchData();
    } catch (err) {
      trackEvent('form_submit_failure', { pin_type: 'spot', error_code: mapApiErrorToCode(err) });
      throw err;
    } finally {
      setFormLoading(false);
    }
  };

  const handleSeekerSubmit = async (data: Omit<SeekerPin, 'id' | 'created_at' | 'comment_count'> & { email: string; phone: string }) => {
    trackEvent('form_submit_attempt', { pin_type: 'seeker' });
    setFormLoading(true);
    try {
      await api.createSeeker(data);
      trackEvent('form_submit_success', { pin_type: 'seeker' });
      setFormMode(null);
      setFormLatLng(null);
      setViewMode('seekers');
      showSuccess('Seeker pin dropped! We\'ll email you matches.');
      fetchData();
    } catch (err) {
      trackEvent('form_submit_failure', { pin_type: 'seeker', error_code: mapApiErrorToCode(err) });
      throw err;
    } finally {
      setFormLoading(false);
    }
  };

  const handleRentPinSubmit = async (data: Omit<RentPin, 'id' | 'created_at' | 'comment_count'>) => {
    trackEvent('form_submit_attempt', { pin_type: 'rent' });
    setFormLoading(true);
    try {
      await api.createRentPin(data);
      trackEvent('form_submit_success', { pin_type: 'rent' });
      setFormMode(null);
      setFormLatLng(null);
      setViewMode('rent');
      showSuccess('Price data pinned!');
      fetchData();
    } catch (err) {
      trackEvent('form_submit_failure', { pin_type: 'rent', error_code: mapApiErrorToCode(err) });
      throw err;
    } finally {
      setFormLoading(false);
    }
  };

  const handleSearchSelect = (result: GeocodeResult) => {
    setViewport({ center: [result.lng, result.lat], zoom: CITY_ZOOM });
  };

  const handleDetectLocation = async () => {
    trackEvent('location_detect_attempt', { source: 'gps' });
    setLocationLoading(true);
    showSuccess('Detecting your location...');
    try {
      const coords = await detectGpsLocation();
      if (!isWithinIndia(coords.lat, coords.lng)) {
        trackEvent('location_detect_outcome', { source: 'gps', result: 'outside_india' });
        throw new Error('Detected location is outside India.');
      }
      const location = saveLocation({ ...coords, source: 'gps' });
      setUserLocation(location);
      setViewport(getViewportForLocation(location));
      trackEvent('location_detect_outcome', { source: 'gps', result: 'success' });
      showSuccess('Showing parking near your location');
    } catch (err) {
      let result: 'denied' | 'error' = 'error';
      if (err instanceof GeolocationPositionError && err.code === err.PERMISSION_DENIED) {
        result = 'denied';
      }
      trackEvent('location_detect_outcome', { source: 'gps', result });
      showSuccess('Could not get your location. Showing all of India.');
    } finally {
      setLocationLoading(false);
    }
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'security') return (value as string[]).length > 0;
    if (typeof value === 'boolean') return value;
    return value !== '';
  }).length;

  const renderFormModal = () => {
    if (!formMode || !formLatLng) return null;

    const formId = `${formMode}-form`;
    const title =
      formMode === 'spot' ? 'List Your Parking Spot' :
      formMode === 'seeker' ? 'Find Parking Nearby' :
      'Pin What You Pay';
    const submitLabel =
      formMode === 'spot' ? (formLoading ? 'Listing...' : 'List My Spot') :
      formMode === 'seeker' ? (formLoading ? 'Dropping...' : 'Drop Seeker Pin') :
      (formLoading ? 'Pinning...' : 'Pin Rent Data');
    const submitColor =
      formMode === 'spot' ? 'bg-parksy-600 hover:bg-parksy-700' :
      formMode === 'seeker' ? 'bg-blue-600 hover:bg-blue-700' :
      'bg-purple-600 hover:bg-purple-700';

    return (
      <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
          <div className="flex-shrink-0 p-4 border-b flex items-center justify-between bg-gray-50 rounded-t-2xl">
            <h2 className="font-semibold text-gray-900">{title}</h2>
            <button onClick={cancelForm} className="text-gray-500 hover:text-gray-700 text-xl leading-none">&times;</button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 min-h-0">
            {formMode === 'spot' && (
              <SpotForm id={formId} lat={formLatLng.lat} lng={formLatLng.lng} onSubmit={handleSpotSubmit} />
            )}
            {formMode === 'seeker' && (
              <SeekerForm id={formId} lat={formLatLng.lat} lng={formLatLng.lng} onSubmit={handleSeekerSubmit} />
            )}
            {formMode === 'rent' && (
              <RentPinForm id={formId} lat={formLatLng.lat} lng={formLatLng.lng} onSubmit={handleRentPinSubmit} />
            )}
          </div>
          <div className="flex-shrink-0 p-4 border-t bg-gray-50 rounded-b-2xl flex gap-3">
            <button
              type="button"
              onClick={cancelForm}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              form={formId}
              disabled={formLoading}
              className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${submitColor}`}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen w-screen relative overflow-hidden bg-gray-100">
      {/* Success toast */}
      {successMsg && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-parksy-600 text-white px-4 py-2 rounded-full shadow-lg z-[120] text-sm font-medium">
          {successMsg}
        </div>
      )}

      {/* Top bar: logo, search, locate, menu */}
      <div
        className="absolute left-4 right-4 z-[120] flex items-start justify-between gap-3 pointer-events-none"
        style={{ top: 'max(env(safe-area-inset-top, 0px) + 1rem, 1rem)' }}
      >
        <div className="flex items-start gap-2 pointer-events-auto min-w-0">
          <Link
            to="/"
            className="floating-panel flex items-center gap-1.5 font-bold text-gray-900 whitespace-nowrap pl-3 pr-3 py-2"
          >
            <span className="text-parksy-600">🅿️</span>
            <span className="hidden sm:inline">parksy</span>
            <span className="text-parksy-600 hidden sm:inline">.rent</span>
          </Link>
          <LocationSearch onSelect={handleSearchSelect} />
          <div className="floating-panel flex items-center px-1.5 py-2">
            <HelpTip tipKey="search" label="Search examples" />
          </div>
        </div>

        <div className="flex items-center gap-2 pointer-events-auto flex-shrink-0">
          <button
            onClick={handleDetectLocation}
            disabled={locationLoading}
            className="floating-panel p-2.5 text-gray-700 hover:text-gray-900 disabled:opacity-60"
            aria-label="Use my location"
            title="Use my location"
            data-tour="locate"
          >
            {locationLoading ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-parksy-600" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>

          <button
            onClick={() => setTourStart()}
            className="floating-panel p-2.5 text-gray-700 hover:text-gray-900"
            aria-label="Take a tour"
            title="Take a tour"
            data-testid="tour-help-button"
          >
            <span className="text-base font-semibold leading-none">?</span>
          </button>

          <button
            onClick={() => setDrawerOpen(true)}
            className="floating-panel p-2.5 text-gray-700 hover:text-gray-900 relative"
            aria-label="Open menu"
            data-tour="menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-parksy-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Drawer */}
      <aside
        className={`absolute top-0 left-0 z-40 w-80 h-full bg-white shadow-2xl transform transition-transform duration-200 overflow-y-auto scrollbar-hide ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 space-y-4">
          {/* Drawer header */}
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1.5 font-bold text-xl text-gray-900">
              <span className="text-parksy-600">🅿️</span>
              <span>parksy</span>
              <span className="text-parksy-600">.rent</span>
            </Link>
            <button
              onClick={() => setDrawerOpen(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none p-1"
              aria-label="Close menu"
            >
              &times;
            </button>
          </div>

          {/* Stats */}
          <div className="bg-navy-900 text-white rounded-xl p-4">
            <h2 className="font-bold text-lg mb-3">India's Parking Map</h2>
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-xl font-bold text-parksy-400">{stats.parking_spots}</div>
                <div className="text-xs opacity-80">Spots</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-xl font-bold text-blue-400">{stats.seeker_pins}</div>
                <div className="text-xs opacity-80">Seekers</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-xl font-bold text-purple-400">{stats.rent_pins}</div>
                <div className="text-xs opacity-80">Price Pins</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-xl font-bold text-amber-400">{stats.matches}</div>
                <div className="text-xs opacity-80">Matches</div>
              </div>
            </div>
          </div>

          {/* View mode tabs */}
          <div>
            <div className="flex items-center justify-between mb-1.5 px-1">
              <span className="text-xs font-medium text-gray-500">Show on map</span>
              <HelpTip tipKey="view_modes" label="Layer meanings" />
            </div>
            <div className="bg-gray-50 rounded-xl p-1 flex" data-tour="view-modes">
              {(['all', 'spots', 'seekers', 'rent'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewModeTracked(mode)}
                  className={`flex-1 py-2 text-xs font-medium rounded-lg transition ${
                    viewMode === mode
                      ? 'bg-navy-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {mode === 'all' && 'All'}
                  {mode === 'spots' && 'Spots'}
                  {mode === 'seekers' && 'Seekers'}
                  {mode === 'rent' && 'Prices'}
                </button>
              ))}
            </div>
          </div>

          {/* Filters */}
          <FilterPanel
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters(DEFAULT_FILTERS)}
          />

          {/* Loading */}
          {loading && (
            <div className="text-center text-sm text-gray-500 py-2">
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-parksy-600 mr-2"></span>
              Loading...
            </div>
          )}

          {/* Page links */}
          <div className="border-t pt-4">
            <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">More</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                  setTourStart();
                }}
                className="text-left text-parksy-600 hover:text-parksy-700 font-medium"
                data-testid="drawer-take-tour"
              >
                Take a tour
              </button>
              <Link to="/about" onClick={() => setDrawerOpen(false)} className="text-gray-700 hover:text-parksy-600">About</Link>
              <Link to="/faq" onClick={() => setDrawerOpen(false)} className="text-gray-700 hover:text-parksy-600">FAQ</Link>
              <Link to="/privacy" onClick={() => setDrawerOpen(false)} className="text-gray-700 hover:text-parksy-600">Privacy</Link>
              <Link to="/terms" onClick={() => setDrawerOpen(false)} className="text-gray-700 hover:text-parksy-600">Terms</Link>
              <Link to="/contact" onClick={() => setDrawerOpen(false)} className="text-gray-700 hover:text-parksy-600">Contact</Link>
            </div>
            <p className="mt-4 text-xs text-gray-400">
              © {new Date().getFullYear()} Parksy.rent. Free forever. No brokerage.
            </p>
          </div>
        </div>
      </aside>

      {/* Drawer overlay */}
      {drawerOpen && (
        <div
          className="absolute inset-0 bg-black/20 z-30"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Map */}
      <div className="w-full h-full">
        <Map
          spots={spots}
          seekers={seekers}
          rentPins={rentPins}
          viewMode={viewMode}
          onMapClick={formMode ? handleMapClick : undefined}
          isAddingPin={!!formMode && !formLatLng}
          initialCenter={viewport.center}
          initialZoom={viewport.zoom}
          focusedPin={focusedPin}
          onReady={setMapReady}
        />
      </div>

      {/* Bottom-left: legend toggle */}
      <div
        className="absolute left-4 z-[120]"
        style={{ bottom: 'max(env(safe-area-inset-bottom, 0px) + 1rem, 4rem)' }}
        data-tour="legend"
      >
        <button
          onClick={() => setLegendOpen(!legendOpen)}
          className={`floating-panel px-3 py-2 text-sm font-medium text-gray-700 flex items-center gap-2 ${legendOpen ? 'ring-2 ring-parksy-500' : ''}`}
          aria-expanded={legendOpen}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 7m0 13V7m0 0L9 4" />
          </svg>
          Legend
        </button>
        {legendOpen && (
          <div
            data-tour="legend-dropdown"
            className="absolute bottom-full left-0 mb-2 floating-panel p-3 space-y-2 text-xs min-w-[180px]"
          >
            {LEGEND_ITEMS.map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${item.color}`}></span>
                <span className="text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom-right: expandable FAB */}
      {!formMode && (
        <div
          ref={fabRef}
          className="absolute right-4 z-[120] flex flex-col items-end gap-2"
          style={{ bottom: 'max(env(safe-area-inset-bottom, 0px) + 1.5rem, 5rem)' }}
          data-tour="fab"
        >
          {fabOpen && (
            <div className="flex flex-col items-end gap-2 mb-1 animate-fade-in-up">
              {ACTION_ITEMS.map((action) => (
                <button
                  key={action.mode}
                  onClick={() => startAddingPin(action.mode)}
                  className={`${action.color} text-white pl-3 pr-4 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-2 transition transform hover:scale-105`}
                >
                  <span>{action.icon}</span>
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => setFabOpen(!fabOpen)}
            className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white transition-transform duration-200 ${
              fabOpen ? 'bg-gray-800 rotate-45' : 'bg-parksy-600 hover:bg-parksy-700'
            }`}
            aria-label={fabOpen ? 'Close actions' : 'Add pin'}
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      )}

      {/* Form modal */}
      {renderFormModal()}

      {/* Report pin modal */}
      {reportTarget && (
        <ReportPinModal
          pinType={reportTarget.type}
          pinId={reportTarget.id}
          onClose={() => setReportTarget(null)}
          onSubmitted={() => {
            setReportTarget(null);
            showSuccess("Thanks — we'll review this pin.");
          }}
        />
      )}

      {/* Interest modal */}
      {interestTarget && (
        <InterestModal
          spotId={interestTarget.spotId}
          onClose={() => setInterestTarget(null)}
          onSubmitted={(message) => {
            setInterestTarget(null);
            showSuccess(message);
            fetchData();
          }}
        />
      )}

      {/* Comments modal */}
      {commentsTarget && (
        <CommentsModal
          pinType={commentsTarget.pinType}
          pinTypeApi={commentsTarget.pinTypeApi}
          pinId={commentsTarget.pinId}
          onClose={() => setCommentsTarget(null)}
        />
      )}

      <TourOverlay
        step={tour.currentStep}
        stepIndex={tour.stepIndex}
        totalSteps={tour.totalSteps}
        isLast={tour.stepIndex >= tour.totalSteps - 1}
        onNext={tour.next}
        onPrev={tour.prev}
        onDismiss={tour.dismiss}
      />
    </div>
  );
}
